// pages/demo/index.tsx
import React, {useState} from 'react';
import {Box, IconButton, Modal, Stack, Typography,} from '@mui/material';
import {useTheme} from '@mui/material/styles';
import Head from 'next/head';
import Image from 'next/image';
import {ConfettiFireworks} from '../../components/styling/Confetti';
import {useRouter} from 'next/router';
import {enqueueSnackbar, SnackbarProvider} from 'notistack';
import {Close} from '@mui/icons-material';
import {GetServerSideProps} from 'next';

interface SubmitData {
    code: number;
    message: string;
    data: {
        admin_join_link: string;
        finished_interview_data: any[];
        hours_to_expire: number;
        id: string;
        interview_created_at: number;
        interview_date: string;
        interview_link: string;
        interview_start_at: number;
        interview_time: string;
        interview_valid_till: number;
        interviewer_company: string;
        interviewer_email: string;
        interviewer_name: string;
        invitation_sent: boolean;
        project_id: string;
        resume: string;
        room_id: string;
        status: string;
        student_email: string;
        student_name: string;
    };
}

interface Difficulty {
    level: number;
    value: string;
}

interface InterviewProfile {
    active: boolean;
    displayName: string;
    jobDescription?: string;
    topics: string[];
    difficulty: Difficulty[];

    [key: string]: any;
}

interface InterviewProfiles {
    [key: string]: InterviewProfile;
}

interface InterviewFormProps {
    interviewProfiles: InterviewProfiles;
}

const InterviewForm: React.FC<InterviewFormProps> = ({interviewProfiles}) => {
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [interviewType, setInterviewType] = useState('none');
    const [difficultyLevel, setDifficultyLevel] = useState(-1);
    const [gdprConsent, setGdprConsent] = useState(false);
    const [loading, setLoading] = useState(false);
    const [submit, setSubmit] = useState(false);
    const [topics, setTopics] = useState<string[]>([]);
    const [submitData, setSubmitData] = useState<SubmitData | null>(null);
    const theme = useTheme();
    const route = useRouter();

    const snackbarStyle = {
        style: {
            fontSize: 16,
            fontFamily: 'Open Sans',
        },
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (name.trim() === '') {
            enqueueSnackbar('Please enter your name', {
                variant: 'error',
                ...snackbarStyle,
            });
            return;
        }
        if (email.trim() === '') {
            enqueueSnackbar('Please enter your email', {
                variant: 'error',
                ...snackbarStyle,
            });
            return;
        }
        if (interviewType === 'none') {
            enqueueSnackbar('Please select an interview type', {
                variant: 'error',
                ...snackbarStyle,
            });
            return;
        }

        if (interviewProfiles[interviewType].topics.length != 0 && topics.length === 0) {
            enqueueSnackbar('Please select interview topics', {
                variant: 'error',
                ...snackbarStyle,
            });
            return;
        }
        if (interviewProfiles[interviewType].difficulty.length != 0 && difficultyLevel === -1) {
            enqueueSnackbar('Please select difficulty level', {
                variant: 'error',
                ...snackbarStyle,
            });
            return;
        }

        const formData = {name, email, interviewType, difficulty: difficultyLevel, topics, gdprConsent};
        try {
            setLoading(true);
            const response = await fetch('/api/interview/schedule-demo', {
                method: 'POST',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify(formData),
            });
            if (!response.ok) {
                alert('Failed to submit form: ' + await response.text());
                return;
            }
            const result = await response.json();
            if (result.error) {
                alert('Failed to submit form: ' + result.error);
            } else {
                setSubmit(true);
                setSubmitData(result);
                ConfettiFireworks();
            }
        } catch (error) {
            alert('Failed to submit form: ' + error);
        } finally {
            setLoading(false);
        }
    };

    function handleModalClose() {
        setName('');
        setEmail('');
        setLoading(false);
        setTopics([]);
        setInterviewType('none');
        setDifficultyLevel(-1);
        setSubmit(false);
    }

    function handleRedirectInterview() {
        route.push(`/interview/${submitData?.data.room_id}`).then(r => console.log(r));
    }

    return (
        <>
            <Head>
                <title>AltHire AI Demo</title>
            </Head>
            <SnackbarProvider/>
            <Box sx={{
                minHeight: '100vh',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                paddingInline: '10px',
                background: '#111',
            }}>
                <Typography color={'white'} textAlign="center" fontFamily="fantasy" variant="h2">
                    Welcome to <span className="main-logo"></span>
                    <span style={{
                        background: 'linear-gradient(to right, #70e1f5, #ffd194)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                    }}>AltHire AI</span>
                </Typography>
                <Typography color={'white'} textAlign="center" mb={1} variant="subtitle1">
                    Witness the future of hiring with our AI-powered interview platform
                </Typography>
                <Box sx={{
                    color: '#fff',
                    padding: '20px',
                    borderRadius: '10px',
                    border: '1px solid #333',
                    width: 500,
                    [theme.breakpoints.down(700)]: {width: '100%'},
                }}>
                    <Typography textAlign="center" variant="h5">We are currently updating our systems to make you
                        more
                        satisfied.
                        Please check out our demo video for understanding what we can do.</Typography>

                    <iframe width="100%" height="315" src="https://www.youtube.com/embed/Stk3afsAcl8"
                            title="YouTube video player"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen/>
                </Box>
            </Box>
            <Modal open={submit}
                   disableAutoFocus={true}
                   sx={{
                       outline: 0,
                       '&:focus': {
                           outline: 'none',
                           border: 'none',
                       },
                   }}>
                <Box sx={{
                    width: '80%',
                    [theme.breakpoints.up('md')]: {
                        width: '33%',
                    },
                    position: 'relative',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    padding: 3,
                    borderRadius: 2,
                    background: '#111',
                    boxShadow: 'rgba(50, 50, 105, 0.15) 0px 2px 5px 0px, rgba(0, 0, 0, 0.05) 0px 1px 1px 0px',
                    border: '1px solid grey',
                }}>
                    <Stack width={'100%'} alignItems={'end'}>
                        <IconButton sx={{
                            color: '#fff',
                        }} onClick={handleModalClose}>
                            <Close/>
                        </IconButton>
                    </Stack>
                    <Box sx={{
                        position: 'relative',
                        pb: '6rem',
                    }}>
                        <Stack sx={{
                            position: 'absolute',
                            top: '0%',
                            left: '50%',
                            transform: 'translate(-50%,-50%)',
                        }} alignItems={'center'}>
                            <Image style={{
                                background: '#000',
                                borderRadius: '50%',
                            }} width={200} height={200} src={'images/demo/ghost.svg'} alt={
                                'success Image'
                            }/>
                        </Stack>
                    </Box>

                    <Typography sx={{
                        fontSize: 28,
                        fontWeight: 'bold',
                        textAlign: 'center',
                        padding: 2,
                        color: 'white',
                    }}>Interview Scheduled</Typography>
                    <Typography sx={{
                        fontSize: 16,
                        fontFamily: 'Poppins',
                        fontWeight: 'light',
                        color: 'white',
                        letterSpacing: '1px',
                    }}>
                        Your interview has been scheduled successfully. You will receive an email with the details
                        shortly.
                        Also, you can start the interview now or later.
                        For Starting the interview now, click on the <span color={'#1565c0'}>Start Now button</span>

                    </Typography>
                    <Stack gap={2} justifyContent={'right'} pt={4} pb={0} direction={'row'} flexWrap={'wrap'}>

                    </Stack>
                </Box>
            </Modal>
        </>
    );
};

export const getServerSideProps: GetServerSideProps = async () => {
    try {
        const res = await fetch(process.env.API_BASE + '/interview/interview_profiles', {
            method: 'GET',
            headers: {'Content-Type': 'application/json'},
        });
        if (!res.ok) {
            throw new Error('Failed to fetch interview profiles');
        }
        const interviewProfiles = await res.json();
        return {props: {interviewProfiles}};
    } catch (error) {
        console.error('Error fetching interview profiles:', error);
        return {props: {interviewProfiles: {}}};
    }
};

export default InterviewForm;
